// Manage categories (Carla, 2026-10-12): a manager, not only an add. A Sheet on a phone, a Modal on
// desktop. Every category she has in this workspace, the built-ins and her own, each with its note
// count from the server; rename and recolour inline; reorder with ↑/↓ (the tap alternative) and by
// dragging; add; delete behind a confirm that says where the notes go, then behind Undo (the page's
// useDelayedDelete). General is never renamed or deleted; its colour can change.
//
// The page owns the state (Notes.js): the chips this lists are the same chips the page's filter, the
// cards and the editor's picker are drawn from, so a change shows everywhere at once.
import { useState, useRef } from 'react';
import { Modal, Sheet, Button, Input, Banner, v } from '../../ui';
import { TOKENS } from '../../ui/tokens';

// The category swatches the Notes page has always offered (the editor's "+ New category" and this
// manager): the pastel palette, by token; the stored value is the colour itself.
export const PALETTE = [
  ['pastel-blue', 'Blue'], ['pastel-green', 'Green'], ['pastel-mint', 'Mint'], ['pastel-yellow', 'Yellow'],
  ['pastel-orange', 'Orange'], ['pastel-pink', 'Pink'], ['pastel-lilac', 'Lilac'], ['pastel-periwinkle', 'Periwinkle'],
];
export const PRESET_COLOURS = PALETTE.map(([k]) => TOKENS[k]);
export const DEFAULT_COLOUR = TOKENS['pastel-blue'];

// The swatches: one choice, so a radio group.
export function ColourPicker({ value, onChange, label = 'Colour' }) {
  return (
    <div className="nt-swatches" role="radiogroup" aria-label={label}>
      {PRESET_COLOURS.map((col, i) => (
        <button key={col} type="button" role="radio" aria-checked={value === col} aria-label={PALETTE[i][1]}
          className="nt-swatch ui-hit" style={{ '--nt-dot': col }} onClick={() => onChange(col)} />
      ))}
    </div>
  );
}

export const Dot = ({ colour }) => <span className="nt-dot" style={{ '--nt-dot': colour || v('pastel-neutral') }} aria-hidden="true" />;

export const chipColour = (c) => c.colour || v(c.token || 'pastel-neutral');
const editColour = (c) => c.colour || TOKENS[c.token] || DEFAULT_COLOUR;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// "12 notes in Clients will move to General."
export function deleteMessage(entry, count) {
  if (count === undefined || count === null) return `Any notes in ${entry.name} will move to General.`;
  if (count === 0) return `${entry.name} has no notes. Nothing else changes.`;
  return `${plural(count, 'note')} in ${entry.name} will move to General.`;
}

/**
 * open, onClose, phone
 * entries   the chips, in her order (pending deletes already left out)
 * counts    { [key]: n } from the server, or null while loading; countsError: string | null
 * onRetryCounts()
 * onAdd(name, colour) -> category | { failed }
 * onSave(entry, { name?, colour? }) -> null | failure message
 * onMove(key, toIndex) -> null | failure message
 * onDelete(entry)      the delete starts (behind Undo)
 * failures  { [key]: message } for a delete that failed after the Undo window
 */
export default function CategoryManager({
  open, onClose, phone, entries, counts, countsError, onRetryCounts, onAdd, onSave, onMove, onDelete, failures = {},
}) {
  const [edit, setEdit] = useState(null);         // { key, name, colour, busy }
  const [confirm, setConfirm] = useState(null);   // key
  const [errors, setErrors] = useState({});       // key -> message (save or move)
  const [drag, setDrag] = useState(null);         // key being dragged
  const [over, setOver] = useState(null);
  const listRef = useRef(null);

  const close = () => { setEdit(null); setConfirm(null); setErrors({}); onClose(); };
  const setError = (key, message) => setErrors((e) => ({ ...e, [key]: message || undefined }));

  async function move(entry, to) {
    if (to < 0 || to >= entries.length) return;
    setError(entry.key, null);
    const failure = await onMove(entry.key, to);
    if (failure) setError(entry.key, failure);
    // Keep the keyboard on the button that moved it, now in its new row.
    setTimeout(() => {
      const btn = listRef.current?.querySelector(`[data-key="${CSS.escape(entry.key)}"] [data-move="${to > entries.indexOf(entry) ? 'down' : 'up'}"]`);
      if (btn && !btn.disabled) btn.focus();
    }, 0);
  }

  async function save(entry) {
    if (!edit || edit.busy) return;
    const patch = {};
    if (entry.key !== 'general' && edit.name.trim() !== entry.name) patch.name = edit.name.trim();
    if (edit.colour !== editColour(entry)) patch.colour = edit.colour;
    if (!Object.keys(patch).length) { setEdit(null); return; }
    setEdit((e) => ({ ...e, busy: true }));
    setError(entry.key, null);
    const failure = await onSave(entry, patch);
    if (failure) { setError(entry.key, failure); setEdit((e) => (e ? { ...e, busy: false } : e)); return; }
    setEdit(null);
  }

  const rows = entries.map((entry, i) => {
    const editing = edit?.key === entry.key;
    const confirming = confirm === entry.key;
    const count = counts ? (counts[entry.key] ?? 0) : null;
    const general = entry.key === 'general';
    const message = errors[entry.key] || failures[entry.key];
    return (
      <li key={entry.key} data-key={entry.key} data-testid={`manage-cat-${entry.key}`}
        className={`nt-mcat${drag === entry.key ? ' nt-mcat--drag' : ''}${over === entry.key && drag && drag !== entry.key ? ' nt-mcat--over' : ''}`}
        draggable={!editing && !confirming}
        onDragStart={(e) => { setDrag(entry.key); try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', entry.key); } catch { /* the drag still works in this page */ } }}
        onDragOver={(e) => { if (drag) { e.preventDefault(); setOver(entry.key); } }}
        onDragLeave={() => setOver((o) => (o === entry.key ? null : o))}
        onDrop={(e) => { e.preventDefault(); const from = entries.find((x) => x.key === drag); setDrag(null); setOver(null); if (from && from.key !== entry.key) move(from, i); }}
        onDragEnd={() => { setDrag(null); setOver(null); }}>
        <div className="nt-mcat__row">
          <span className="nt-mcat__grip" aria-hidden="true" title="Drag to reorder">⋮⋮</span>
          <Dot colour={chipColour(entry)} />
          <span className="nt-mcat__name">{entry.name}</span>
          <span className="nt-mcat__count">{count === null ? (countsError ? '' : 'Counting…') : plural(count, 'note')}</span>
          <div className="nt-mcat__actions">
            <Button size="sm" variant="tertiary" data-move="up" aria-label={`Move ${entry.name} up`} disabled={i === 0} onClick={() => move(entry, i - 1)}>↑</Button>
            <Button size="sm" variant="tertiary" data-move="down" aria-label={`Move ${entry.name} down`} disabled={i === entries.length - 1} onClick={() => move(entry, i + 1)}>↓</Button>
            <Button size="sm" variant="secondary" aria-label={`Edit ${entry.name}`} aria-expanded={editing}
              onClick={() => { setConfirm(null); setEdit(editing ? null : { key: entry.key, name: entry.name, colour: editColour(entry), busy: false }); }}>Edit</Button>
            {!general && (
              <Button size="sm" variant="destructive" aria-label={`Delete ${entry.name}`} aria-expanded={confirming}
                onClick={() => { setEdit(null); setConfirm(confirming ? null : entry.key); }}>Delete</Button>
            )}
          </div>
        </div>
        {editing && (
          <div className="nt-cat__edit">
            {general
              ? <p className="nt-mcat__note">General can&apos;t be renamed: it is where every note without another category lives.</p>
              : (
                <Input aria-label={`Rename ${entry.name}`} value={edit.name} autoFocus maxLength={60}
                  onChange={(e) => setEdit((x) => ({ ...x, name: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') save(entry); if (e.key === 'Escape') setEdit(null); }} />
              )}
            <ColourPicker label={`Colour of ${entry.name}`} value={edit.colour} onChange={(col) => setEdit((x) => ({ ...x, colour: col }))} />
            <div className="nt-newcat__actions">
              <Button size="sm" variant="tertiary" onClick={() => setEdit(null)}>Cancel</Button>
              <Button size="sm" variant="confirm" disabled={(!general && !edit.name.trim()) || edit.busy} onClick={() => save(entry)}>Save</Button>
            </div>
          </div>
        )}
        {confirming && (
          <div className="nt-mcat__confirm" role="group" aria-label={`Delete ${entry.name}?`}>
            <p className="nt-mcat__confirm-text" data-testid="manage-cat-confirm">{deleteMessage(entry, count)} You can undo it for a few seconds afterwards.</p>
            <div className="nt-newcat__actions">
              <Button size="sm" variant="tertiary" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button size="sm" variant="delete" autoFocus onClick={() => { setConfirm(null); onDelete(entry); }}>Delete {entry.name}</Button>
            </div>
          </div>
        )}
        {message && <p className="nt-failed" role="alert" data-testid={`cat-failed-${entry.key}`}>{message}</p>}
      </li>
    );
  });

  const body = (
    <div className="nt-cats">
      <p className="nt-panel__empty">Rename, recolour, reorder or delete any of these. General always stays: notes from a deleted category move there.</p>
      {countsError && (
        <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={onRetryCounts}>Try again</Button>}>
          The note counts did not load. {countsError}
        </Banner>
      )}
      <ul className="nt-mcats" ref={listRef} aria-label="Your categories">{rows}</ul>
      <div className="nt-cats__add">
        <AddCategoryInline onAdd={onAdd} />
      </div>
    </div>
  );
  const footer = <Button variant="confirm" onClick={close}>Done</Button>;
  if (phone) return <Sheet open={open} onClose={close} title="Manage categories" color="group-my-work" footer={footer}>{body}</Sheet>;
  return <Modal open={open} onClose={close} title="Manage categories" color="group-my-work" footer={footer}>{body}</Modal>;
}

// Add a category (also offered in the editor). A failed add KEEPS what she typed.
export function AddCategoryInline({ onAdd }) {
  const [name, setName] = useState('');
  const [colour, setColour] = useState(DEFAULT_COLOUR);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');

  async function submit() {
    if (!name.trim() || busy) return;
    setBusy(true);
    setFailure('');
    const result = await onAdd(name.trim(), colour);
    if (result && result.failed) setFailure(result.failed);
    else { setName(''); setColour(DEFAULT_COLOUR); }
    setBusy(false);
  }

  return (
    <div className="nt-newcat">
      <Input aria-label="Category name" placeholder="New category name" value={name} maxLength={60}
        onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
      <ColourPicker value={colour} onChange={setColour} />
      <div className="nt-newcat__actions">
        <Button size="sm" variant="create" onClick={submit} disabled={!name.trim() || busy}>+ Add category</Button>
      </div>
      {failure && <p data-testid="add-category-failed" className="nt-failed" role="alert">{failure}</p>}
    </div>
  );
}
